import { createRoot } from "react-dom/client";
import { StrictMode, useEffect, useState } from "react";

import "../styles/app.css";
import { initTheme } from "../lib/theme.ts";
import { authPost } from "../lib/authApi.ts";
import { readNextParam } from "../lib/nextParam.ts";
import { AdminShell, adminNavOf } from "../admin/AdminShell.tsx";
import { CredentialsPage } from "../admin/CredentialsPage.tsx";
import { OperatorSignIn } from "../admin/OperatorSignIn.tsx";
import { OperatorsPage } from "../admin/OperatorsPage.tsx";
import { OrganisationsPage } from "../admin/OrganisationsPage.tsx";
import { PlatformPage } from "../admin/PlatformPage.tsx";
import { SpendPage, spendRequestIdOf } from "../admin/SpendPage.tsx";
import { SpendRequestPage } from "../admin/SpendRequestPage.tsx";

// The admin app's UI entry: the one bundle the admin server serves, `/sign-in`
// to anyone and every other path behind the operator's session. Pathname
// switches and plain anchors, no router library. See ADR-0039.

/** What `/api/me` on the admin app returns: the signed-in operator and the session's expiry. */
export type AdminMe = {
  operator: { email: string };
  session: { expiresAt: string };
};

/**
 * Picks the admin screen by pathname: the operator sign-in at `/sign-in`,
 * otherwise the shell with the page the path names. Mounted at the admin
 * bundle's root.
 */
export function AdminPage({ pathname, search }: { pathname: string; search: string }): JSX.Element {
  const params = new URLSearchParams(search);
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/sign-in") {
    return (
      <div className="auth-wrap">
        <OperatorSignIn next={readNextParam(search) ?? "/"} error={params.get("error")} />
      </div>
    );
  }
  return <AdminApp pathname={path} />;
}

function AdminApp({ pathname }: { pathname: string }): JSX.Element {
  const [me, setMe] = useState<AdminMe | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await fetch("/api/me", { credentials: "same-origin" });
      if (res.status === 401) {
        window.location.assign(`/sign-in?next=${encodeURIComponent(pathname)}`);
        return;
      }
      if (!cancelled && res.ok) setMe((await res.json()) as AdminMe);
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  if (!me) return <p className="page">Loading…</p>;
  const current = adminNavOf(pathname);
  const spendRequest = spendRequestIdOf(pathname);
  return (
    <AdminShell
      operator={me.operator}
      current={current}
      onSignOut={() => {
        void authPost("/sign-out", {}).finally(() => window.location.assign("/sign-in"));
      }}
    >
      {current === "organisations" ? (
        <OrganisationsPage />
      ) : current === "platform" ? (
        <PlatformPage />
      ) : current === "credentials" ? (
        <CredentialsPage />
      ) : current === "operators" ? (
        <OperatorsPage me={me.operator} />
      ) : current === "spend" ? (
        spendRequest ? (
          <SpendRequestPage requestId={spendRequest} />
        ) : (
          <SpendPage />
        )
      ) : undefined}
    </AdminShell>
  );
}

const rootEl = document.getElementById("root");
if (rootEl) {
  initTheme();
  createRoot(rootEl).render(
    <StrictMode>
      <AdminPage pathname={window.location.pathname} search={window.location.search} />
    </StrictMode>,
  );
}
